/**
 * Release Notes Manager — admin-only Type-3 surface at /admin/releases (Day 100).
 *
 * Composes public.release_notes: a newest-first list of all releases (admin reads
 * drafts + published via the is_admin() arm of release_notes_select_published) +
 * a FormModal compose — version (UNIQUE; a 23505 collision surfaces a friendly
 * error, not a raw throw), released_on (the §13 ArbiDatePicker, never
 * input[type=date]), an optional title, and a highlights string-array editor
 * (persists as the jsonb array). NO body field (RBP:1379 = date/version/
 * highlights) and NO markdown renderer — highlights are plain text everywhere
 * (the TopBar "What's new" widget + the arbi-docs Release Notes page).
 *
 * Writes go through the admin's PostgREST session (arbi-dev has no service key —
 * the UserManagement precedent); RLS enforces admin-only write.
 */

import { useState, useEffect, useCallback, type JSX } from 'react';
import { Megaphone, Plus, X } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../lib/supabase';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { FormModal } from '../components/ui/FormModal';
import ArbiToggle from '../components/ui/ArbiToggle';
import ArbiDatePicker from '../components/ui/ArbiDatePicker';
import type { Database } from '../../../../src/lib/database.types';

type ReleaseNote = Database['public']['Tables']['release_notes']['Row'];
type ToastState = { type: 'success' | 'error'; message: string };
type ModalMode = 'add' | 'edit';

interface ReleaseDraft {
  version: string;
  releasedOn: string | null;
  title: string;
  highlights: string[];
  isPublished: boolean;
}

const EMPTY_DRAFT: ReleaseDraft = {
  version: '',
  releasedOn: null,
  title: '',
  highlights: [''],
  isPublished: false,
};

// highlights is a jsonb column (generated type Json) — coerce to a string list.
function toBullets(h: ReleaseNote['highlights']): string[] {
  return Array.isArray(h) ? h.filter((x): x is string => typeof x === 'string') : [];
}

function fmtReleaseDate(iso: string | null): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function draftFromRow(row: ReleaseNote): ReleaseDraft {
  const bullets = toBullets(row.highlights);
  return {
    version: row.version,
    releasedOn: row.released_on,
    title: row.title ?? '',
    highlights: bullets.length ? bullets : [''],
    isPublished: row.is_published,
  };
}

// ── Toast (arbi-dev local pattern, mirror UserManagement) ────────────────────
function Toast({
  toast,
  onDismiss,
}: {
  toast: ToastState | null;
  onDismiss: () => void;
}): JSX.Element | null {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);
  if (!toast) return null;
  const bg =
    toast.type === 'success'
      ? 'bg-success/15 border-success/30 text-success'
      : 'bg-danger/15 border-danger/30 text-danger';
  return (
    <div
      className={clsx(
        'fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg border text-sm font-medium shadow-lg',
        bg
      )}
    >
      {toast.message}
    </div>
  );
}

export default function ReleaseNotesManager(): JSX.Element {
  useDocumentTitle('Release Notes Manager');

  const [rows, setRows] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<ModalMode>('add');
  const [modalRow, setModalRow] = useState<ReleaseNote | null>(null);
  const [draft, setDraft] = useState<ReleaseDraft>(EMPTY_DRAFT);
  const [draftError, setDraftError] = useState<string | null>(null);

  const showToast = useCallback((t: ToastState): void => setToast(t), []);

  const loadRows = useCallback(async (): Promise<void> => {
    setLoading(true);
    const { data, error } = await supabase
      .from('release_notes')
      .select('*')
      .order('released_on', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) {
      showToast({ type: 'error', message: `Failed to load releases — ${error.message}` });
      setLoading(false);
      return;
    }
    setRows(data ?? []);
    setLoading(false);
  }, [showToast]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  function updateDraft<K extends keyof ReleaseDraft>(key: K, value: ReleaseDraft[K]): void {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function openAdd(): void {
    setModalMode('add');
    setModalRow(null);
    setDraft(EMPTY_DRAFT);
    setDraftError(null);
    setModalOpen(true);
  }

  function openEdit(row: ReleaseNote): void {
    setModalMode('edit');
    setModalRow(row);
    setDraft(draftFromRow(row));
    setDraftError(null);
    setModalOpen(true);
  }

  function closeModal(): void {
    if (busy) return;
    setModalOpen(false);
    setDraft(EMPTY_DRAFT);
    setDraftError(null);
  }

  function updateHighlight(index: number, value: string): void {
    setDraft((d) => ({ ...d, highlights: d.highlights.map((h, j) => (j === index ? value : h)) }));
  }
  function addHighlight(): void {
    setDraft((d) => ({ ...d, highlights: [...d.highlights, ''] }));
  }
  function removeHighlight(index: number): void {
    setDraft((d) => {
      const next = d.highlights.filter((_, j) => j !== index);
      return { ...d, highlights: next.length ? next : [''] };
    });
  }

  async function submit(): Promise<void> {
    const version = draft.version.trim();
    const releasedOn = draft.releasedOn;
    if (!version) {
      setDraftError('Enter a version');
      return;
    }
    if (!releasedOn) {
      setDraftError('Pick a release date');
      return;
    }
    const highlights = draft.highlights.map((s) => s.trim()).filter(Boolean);

    setBusy(true);
    setDraftError(null);

    const payload = {
      version,
      released_on: releasedOn,
      title: draft.title.trim() || null,
      highlights,
      is_published: draft.isPublished,
    };

    if (modalMode === 'add') {
      const { error } = await supabase.from('release_notes').insert(payload);
      setBusy(false);
      if (error) {
        setDraftError(
          error.code === '23505'
            ? `Version "${version}" already exists. Use a different version.`
            : `Failed to create — ${error.message}`
        );
        return;
      }
      showToast({ type: 'success', message: `Release ${version} created` });
    } else {
      if (!modalRow) {
        setBusy(false);
        return;
      }
      const { error } = await supabase.from('release_notes').update(payload).eq('id', modalRow.id);
      setBusy(false);
      if (error) {
        setDraftError(
          error.code === '23505'
            ? `Version "${version}" already exists. Use a different version.`
            : `Failed to save — ${error.message}`
        );
        return;
      }
      showToast({ type: 'success', message: 'Release updated' });
    }

    setModalOpen(false);
    setDraft(EMPTY_DRAFT);
    setDraftError(null);
    await loadRows();
  }

  async function togglePublished(row: ReleaseNote): Promise<void> {
    const { error } = await supabase
      .from('release_notes')
      .update({ is_published: !row.is_published })
      .eq('id', row.id);
    if (error) {
      showToast({ type: 'error', message: error.message });
      return;
    }
    await loadRows();
    showToast({ type: 'success', message: row.is_published ? 'Unpublished' : 'Published' });
  }

  const canSave = !busy && Boolean(draft.version.trim()) && Boolean(draft.releasedOn);
  const publishedCount = rows.filter((r) => r.is_published).length;

  return (
    <div className="h-full overflow-auto">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      <FormModal
        open={modalOpen}
        title={modalMode === 'add' ? 'New release note' : `Edit ${modalRow?.version ?? 'release'}`}
        size="lg"
        saving={busy}
        onClose={closeModal}
        footer={
          <>
            <button onClick={closeModal} className="arbi-btn-ghost text-sm" type="button">
              Cancel
            </button>
            <button
              onClick={() => void submit()}
              disabled={!canSave}
              className={clsx('arbi-btn-primary text-sm', !canSave && 'opacity-50 cursor-not-allowed')}
              type="button"
            >
              {busy ? 'Saving…' : modalMode === 'add' ? 'Create' : 'Save'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          {draftError && <p className="text-xs text-danger">{draftError}</p>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="rn-version"
                className="text-2xs uppercase tracking-wider text-ink-muted block mb-1"
              >
                Version
              </label>
              <input
                id="rn-version"
                type="text"
                value={draft.version}
                onChange={(e) => updateDraft('version', e.target.value)}
                placeholder="e.g. 1.4.0"
                className="arbi-input w-full text-sm font-mono"
              />
            </div>
            <div>
              <span className="text-2xs uppercase tracking-wider text-ink-muted block mb-1">
                Release date
              </span>
              <ArbiDatePicker
                value={draft.releasedOn}
                onChange={(v) => updateDraft('releasedOn', v)}
                max={new Date().toISOString().slice(0, 10)}
                className="w-full"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="rn-title"
              className="text-2xs uppercase tracking-wider text-ink-muted block mb-1"
            >
              Title <span className="normal-case text-ink-muted">(optional)</span>
            </label>
            <input
              id="rn-title"
              type="text"
              value={draft.title}
              onChange={(e) => updateDraft('title', e.target.value)}
              placeholder="e.g. Buying Session improvements"
              className="arbi-input w-full text-sm"
            />
          </div>

          <div>
            <span className="text-2xs uppercase tracking-wider text-ink-muted block mb-1">
              Highlights
            </span>
            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-0.5">
              {draft.highlights.map((h, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={h}
                    onChange={(e) => updateHighlight(i, e.target.value)}
                    placeholder="What changed…"
                    aria-label={`Highlight ${i + 1}`}
                    className="arbi-input flex-1 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeHighlight(i)}
                    aria-label={`Remove highlight ${i + 1}`}
                    className="text-ink-muted hover:text-danger transition-colors p-1 flex-shrink-0"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addHighlight}
              className="arbi-btn-ghost text-xs inline-flex items-center gap-1 mt-1.5"
            >
              <Plus size={12} /> Add highlight
            </button>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <ArbiToggle
              checked={draft.isPublished}
              onChange={(v) => updateDraft('isPublished', v)}
              label="Published"
            />
            <span className="text-xs text-ink-secondary">{draft.isPublished ? 'Published' : 'Draft'}</span>
            <span className="text-2xs text-ink-muted">
              {draft.isPublished
                ? 'Visible in the app + on arbi-docs.'
                : 'Hidden until published.'}
            </span>
          </div>
        </div>
      </FormModal>

      <div className="max-w-5xl mx-auto p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-ink-bright">Release Notes Manager</h1>
            <p className="text-xs text-ink-muted">
              {rows.length === 1 ? '1 release' : `${rows.length} releases`}
              {rows.length > 0 ? ` · ${publishedCount} published` : ''}
            </p>
          </div>
          <button
            onClick={openAdd}
            className="arbi-btn-primary text-xs inline-flex items-center gap-1"
            type="button"
          >
            <Plus size={13} /> New release
          </button>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-xs text-ink-muted py-4">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="arbi-card p-8 text-center">
            <Megaphone size={20} className="mx-auto text-ink-muted mb-2" />
            <p className="text-sm text-ink-secondary">No releases yet</p>
            <p className="text-xs text-ink-muted mt-1">
              Compose the first release note — it appears in the app&apos;s &ldquo;What&apos;s
              new&rdquo; widget and on arbi-docs once published.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((row) => {
              const bullets = toBullets(row.highlights);
              return (
                <div key={row.id} className="py-2.5 px-3 bg-surface-2 rounded-lg">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-sm font-medium text-ink-primary">
                        {row.version}
                      </span>
                      {row.title && (
                        <span className="text-sm text-ink-secondary truncate">{row.title}</span>
                      )}
                      <span className="text-2xs text-ink-muted whitespace-nowrap">
                        {fmtReleaseDate(row.released_on)}
                      </span>
                      <span
                        className={clsx(
                          'px-1.5 py-0.5 rounded text-2xs font-medium',
                          row.is_published
                            ? 'bg-success/15 text-success'
                            : 'bg-surface-4 text-ink-muted'
                        )}
                      >
                        {row.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <button
                        onClick={() => openEdit(row)}
                        className="arbi-btn-ghost text-xs px-2 py-1"
                        type="button"
                      >
                        Edit
                      </button>
                      <ArbiToggle
                        checked={row.is_published}
                        onChange={() => void togglePublished(row)}
                        label={`Toggle published: ${row.version}`}
                      />
                    </div>
                  </div>
                  {bullets.length > 0 && (
                    <ul className="mt-1.5 ml-1 space-y-0.5">
                      {bullets.slice(0, 4).map((b, i) => (
                        <li key={i} className="text-2xs text-ink-muted flex gap-1.5">
                          <span aria-hidden="true">•</span>
                          <span className="truncate">{b}</span>
                        </li>
                      ))}
                      {bullets.length > 4 && (
                        <li className="text-2xs text-ink-muted ml-3.5">
                          +{bullets.length - 4} more
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
